import { Injectable, NgZone } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LoaderService } from './loader.service';
import { SKIP_LOADER, SKIP_LOADER_HEADER } from '../http-context.util';

@Injectable()
export class LoadingInterceptor implements HttpInterceptor {
  private requests: HttpRequest<any>[] = [];

  constructor(
    private ngZone: NgZone,
    private loaderService: LoaderService
  ) {}

  private getRequestUrl(req: HttpRequest<unknown>): string {
    return typeof req.url === 'string' ? req.url : String(req.url ?? '');
  }

  private shouldSkipLoader(req: HttpRequest<unknown>): boolean {
    if (req.context.get(SKIP_LOADER)) {
      return true;
    }
    if (req.headers.get(SKIP_LOADER_HEADER) === 'true') {
      return true;
    }

    const url = this.getRequestUrl(req);
    if (
      url.includes('pdf-status') ||
      url.includes('getsitevisitreportpdf')
    ) {
      return true;
    }

    const isAbsolute =
      url.startsWith('https://') || url.startsWith('http://');
    if (isAbsolute && !url.startsWith(environment.apiDomain)) {
      return true;
    }

    return false;
  }

  private updateLoaderVisibility(): void {
    this.ngZone.run(() => {
      const show =
        this.requests.length > 0 || this.loaderService.isAsyncDownloadActive();
      this.loaderService.isLoading.next(show);
    });
  }

  removeRequest(req: HttpRequest<unknown>) {
    const i = this.requests.indexOf(req);
    if (i >= 0) {
      this.requests.splice(i, 1);
    }
    this.updateLoaderVisibility();
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const skipLoader = this.shouldSkipLoader(req);

    if (!skipLoader) {
      this.requests.push(req);
      this.updateLoaderVisibility();
    }
    return new Observable((observer) => {
      const subscription = next.handle(req).subscribe({
        next: (event) => {
          if (event instanceof HttpResponse && !skipLoader) {
            this.removeRequest(req);
          }
          observer.next(event);
        },
        error: (err) => {
          if (!skipLoader) {
            this.removeRequest(req);
          }
          observer.error(err);
        },
        complete: () => {
          if (!skipLoader) {
            this.removeRequest(req);
          }
          observer.complete();
        },
      });

      return () => {
        if (!skipLoader) {
          this.removeRequest(req);
        }
        subscription.unsubscribe();
      };
    });
  }
}
